/* Serialize Shopify cart requests and remove orphaned AMP add-ons. */
(() => {
  if (window.__upliefCartRequests || !window.fetch) return;
  const nativeFetch = window.fetch.bind(window);
  let tail = Promise.resolve(), pending = 0, generation = 0;
  let checkoutBusy = false, replay = false;
  let pruneTimer, pruning = false, pruningPromise;
  const ampPrefix = 'eyJ0IjoiRW0i'; // base64 prefix of AMP embedded offer {"t":"Em"
  const productIds = {
    'premium-bamboo-gloves': 10104268161323,
    'premium-bamboo-knee-sleeves': 10123399495979,
    'premium-anti-fatigue-bamboo-footsleeves': 10135583850795,
    'premium-bamboo-compression-socks': 10176718111019,
    'premium-bamboo-leg-sleeves': 10358295593259
  };
  const kneeSources = [
    'premium-bamboo-gloves', 'premium-anti-fatigue-bamboo-footsleeves',
    'premium-bamboo-compression-socks', 'premium-bamboo-leg-sleeves'
  ];
  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
  const cartUrl = path => `${window.Shopify?.routes?.root || '/'}cart${path ? `/${path}` : ''}.js`;
  function orphanedAddonKeys(cart) {
    const items = cart?.items || [];
    const ordinaryIds = new Set(items.filter(item =>
      !String(item.properties?._amp_u || '').startsWith(ampPrefix) &&
      !item.properties?._uplief_addon_source
    ).map(item => Number(item.product_id)));
    return items.filter(item => {
      if (!String(item.properties?._amp_u || '').startsWith(ampPrefix)) return false;
      const id = Number(item.product_id);
      if (id !== productIds['premium-bamboo-gloves'] && id !== productIds['premium-bamboo-knee-sleeves']) return false;
      const source = item.properties?._uplief_addon_source;
      const validSource = id === productIds['premium-bamboo-gloves']
        ? source === 'premium-bamboo-knee-sleeves'
        : kneeSources.includes(source);
      // Older carts have no source property. Require any eligible ordinary main
      // for those lines; new lines require the exact source chosen on the page.
      const eligible = validSource ? [source]
        : id === productIds['premium-bamboo-gloves'] ? ['premium-bamboo-knee-sleeves'] : kneeSources;
      return !eligible.some(handle => ordinaryIds.has(productIds[handle]));
    }).map(item => item.key);
  }
  async function pruneOrphanedAddons(cart) {
    if (pruningPromise) return pruningPromise;
    const work = (async () => {
      const current = cart || await window.fetch(cartUrl(''), {cache:'no-store'}).then(response => response.json());
      const keys = orphanedAddonKeys(current);
      if (!keys.length) return {removed:0, cart:current};
      pruning = true;
      try {
        const updates = Object.fromEntries(keys.map(key => [key, 0]));
        const response = await window.fetch(cartUrl('update'), {
          method:'POST', headers:{'Content-Type':'application/json', 'Accept':'application/json'},
          body:JSON.stringify({updates})
        });
        if (!response.ok) throw new Error('Unable to update your cart. Please try again.');
        const updated = await response.json();
        if (orphanedAddonKeys(updated).some(key => keys.includes(key))) throw new Error('Unable to remove the discounted add-on. Please try again.');
        if (root()?.querySelector('.opus-drawer-container.open')) {
          if (typeof window.opusRefreshCart === 'function') {
            void Promise.resolve(window.opusRefreshCart()).catch(() => {});
          } else window.dispatchEvent(new Event('ocu:cart:open:updated'));
          setTimeout(() => status('We removed the 50% off add-on because its matching product is no longer in your cart.'), 450);
        }
        return {removed:keys.length, cart:updated};
      } finally { pruning = false; }
    })();
    pruningPromise = work;
    try { return await work; } finally { if (pruningPromise === work) pruningPromise = null; }
  }
  function schedulePrune() {
    if (pruneTimer || pruning) return;
    pruneTimer = setTimeout(() => {
      pruneTimer = null;
      void pruneOrphanedAddons().catch(() => {});
    }, 60);
  }
  const isCart = input => {
    try {
      const url = new URL(input instanceof Request ? input.url : input, location.href);
      return url.origin === location.origin && /\/(?:cart\.js|cart\/(?:add|change|update|clear)(?:\.js)?)$/.test(url.pathname);
    } catch { return false; }
  };
  window.fetch = function (input, init) {
    if (!isCart(input)) return nativeFetch(input, init);
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url = new URL(input instanceof Request ? input.url : input, location.href);
    if ((method === 'GET' || method === 'HEAD') && /\/cart\.js$/.test(url.pathname)) {
      // Repeated drawer opens may request cart.js several times. Reads can run
      // together, but a read following a cart write waits for that write.
      return pending ? tail.then(() => nativeFetch(input, init)) : nativeFetch(input, init);
    }
    pending++; generation++;
    const watchForOrphans = !pruning && method !== 'GET' && method !== 'HEAD' &&
      /\/cart\/(?:change|update)(?:\.js)?$/.test(url.pathname);
    const operation = tail.then(async () => {
      const response = await nativeFetch(input, init);
      // Ego can render the quantity result as soon as Shopify responds. Reading
      // a second copy of every response used to hold the entire write queue.
      // Inspect only successful changes, and do that without delaying Ego.
      if (watchForOrphans && response.ok) {
        void response.clone().json().then(updated => {
          if (orphanedAddonKeys(updated).length) schedulePrune();
        }).catch(() => { /* A non-cart response must not interrupt the app request. */ });
      }
      return response;
    });
    tail = operation.then(() => {}, () => {}).then(() => { pending--; });
    return operation;
  };
  // Ego renders inside a shadow root in most browsers, but uses a light-DOM
  // drawer in some in-app browsers (including Facebook). Both need the same
  // checkout readiness checks and visible error state.
  const root = () => document.querySelector('#opus-shadow-container')?.shadowRoot
    || document.querySelector('#opus-drawer-id');
  const button = () => root()?.querySelector('.cd-checkout-section-button');
  function status(message) {
    const target = button();
    if (!target) return;
    let node = root().querySelector('.uplief-checkout-status');
    if (!node) {
      node = document.createElement('p'); node.className = 'uplief-checkout-status';
      node.setAttribute('role', 'status'); node.setAttribute('aria-live', 'polite');
      node.style.cssText = 'margin:8px 0;text-align:center;font-size:13px;line-height:1.4';
      target.after(node);
    }
    node.textContent = message; node.hidden = !message;
  }
  function reset() {
    checkoutBusy = false; replay = false;
    button()?.removeAttribute('aria-busy');
    status('');
  }
  async function idle(deadline) {
    while (Date.now() < deadline) {
      const seen = generation;
      await Promise.race([tail, delay(100)]);
      if (!pending) {
        await delay(250);
        const changing = root()?.querySelector('.cd-product-item[aria-busy="true"], .cd-modal-content [aria-busy="true"]');
        if (!pending && seen === generation && !changing) return;
      }
    }
    throw new Error('Your cart is still updating. Please try checkout again in a moment.');
  }
  async function checkout() {
    const deadline = Date.now() + 15000;
    try {
      status('Updating your cart…'); button()?.setAttribute('aria-busy', 'true');
      let cart;
      while (Date.now() < deadline) {
        await idle(deadline);
        const response = await window.fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {cache:'no-store'});
        if (!response.ok) throw new Error('Unable to check your cart. Please try checkout again.');
        cart = await response.json();
        const seen = generation;
        await idle(deadline);
        if (seen === generation) break;
        cart = null;
      }
      if (!cart) throw new Error('Your cart is still updating. Please try checkout again in a moment.');
      const cleaned = await pruneOrphanedAddons(cart);
      if (cleaned.removed) {
        checkoutBusy = false;
        button()?.removeAttribute('aria-busy');
        status('We removed the 50% off add-on because its matching product is no longer in your cart. Review your cart, then tap checkout.');
        return;
      }
      if (!cart.item_count) throw new Error('Your cart is empty. Please add an item before checking out.');
      const target = button();
      if (!target) throw new Error('Please reopen your cart and try checkout again.');
      status('Opening checkout…');
      // Preserve Ego's own checkout handler, discount handling and destination.
      replay = true;
      try { target.click(); } finally { replay = false; }
      // Allow recovery if the app fails to navigate; pageshow also resets after Back.
      setTimeout(() => { if (checkoutBusy) reset(); }, 12000);
    } catch (error) {
      checkoutBusy = false; button()?.removeAttribute('aria-busy');
      status(error.message || 'Unable to open checkout. Please try again.');
    }
  }
  document.addEventListener('click', event => {
    if (replay || !event.composedPath().some(el => el?.matches?.('.cd-checkout-section-button'))) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    void checkout();
  }, true);
  window.addEventListener('pageshow', reset);
  window.__upliefCartRequests = {get pending() {return pending;}, get checkoutBusy() {return checkoutBusy;}};

  // Ego's light-DOM drawer does not receive the Custom script that formats the
  // reward message in its shadow drawer. Keep the two render modes consistent.
  const lightRewardStyle = document.createElement('style');
  lightRewardStyle.textContent = '#opus-drawer-id .rewards-text.uplief-reward-hidden{display:none!important}#opus-drawer-id .uplief-reward-copy{margin:0;text-align:center;line-height:1.4}';
  document.head.append(lightRewardStyle);
  let rewardPolls = 0;
  const rewardPoll = setInterval(() => {
    const drawer = document.querySelector('#opus-drawer-id');
    if (!drawer && ++rewardPolls < 60) return;
    clearInterval(rewardPoll);
    if (!drawer) return;
    let queued = false;
    const renderReward = () => {
      const reward = drawer.querySelector('.rewards-text');
      if (!reward) return;
      const giftAdded = !!drawer.querySelector('.cd-product-item-free-gift');
      const remaining = reward.textContent.match(/Spend\s+(?:S)?\$([\d,]+(?:\.\d+)?)/i);
      const rounded = remaining ? Math.ceil(Number(remaining[1].replace(/,/g, ''))) : null;
      const copy = giftAdded ? 'Your FREE Mystery Gift has been added to cart!'
        : rounded !== null ? `Add $${rounded.toLocaleString('en-SG')} to unlock FREE Mystery Gift worth $58!`
        : 'Your FREE Mystery Gift is unlocked!';
      let display = drawer.querySelector('.uplief-reward-copy');
      if (!display) {
        display = document.createElement('p');
        display.className = 'uplief-reward-copy';
        reward.after(display);
      }
      if (display.textContent !== copy) display.textContent = copy;
      reward.classList.add('uplief-reward-hidden');
      reward.setAttribute('aria-hidden', 'true');
    };
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; renderReward(); });
    }).observe(drawer, {childList:true, subtree:true, characterData:true});
    renderReward();
  }, 250);
})();
